import * as React from 'react';
import { SendHorizonal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface PromptInputProps {
  value: string;
  onValueChange: (next: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Sticky composer at the bottom of the chat. Submits on Cmd/Ctrl+Enter and
 * via the send button. Auto-grows up to 200px.
 */
export function PromptInput({
  value,
  onValueChange,
  onSubmit,
  disabled,
  placeholder = 'Send a message…',
  className,
}: PromptInputProps) {
  const ref = React.useRef<HTMLTextAreaElement | null>(null);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [value]);

  const submit = () => {
    if (disabled || !value.trim()) return;
    onSubmit();
  };

  return (
    <div className={cn('flex items-end gap-2', className)}>
      <Textarea
        ref={ref}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder={placeholder}
        rows={1}
        className="min-h-[40px] resize-none"
      />
      <Button
        onClick={submit}
        disabled={disabled || !value.trim()}
        size="icon"
        className="size-10 shrink-0"
      >
        <SendHorizonal />
      </Button>
    </div>
  );
}
